import {AbstractParameter} from './AbstractParameter.js';
import KeyScaling from './KeyScaling.js';
import {A_RATE, K_RATE} from 'constants/automation-rates.js';
import Tone from 'tone';
import {convertPatternToValue} from './convertPatternToValue.js';
import Automations from './Automations.js';
import {convertValue} from './convertPatternToValue.js';

const WAIT_TIME = 200;

class AbstractAudioParamParameter extends AbstractParameter {
    constructor(name, audioParam, setter, config={}) {
        super(name, audioParam, setter, config);
        this.hasAudioParamSetter = true;
        this.setAudioParam(audioParam);
    }

    fromJson(json, sequencerEngine, isCopy=false) {
        if (json.depth) {
            this._depth = json.depth.unlockedValue;
        }

        let oldId = this.id;
        let bulk = {};
        let subSequence = this.context && this.context.subSequence ?
            this.context.subSequence :
            sequencerEngine !== undefined ?
            sequencerEngine.currentSubSequence || sequencerEngine.subSequences[0] :
            undefined;

        if (json.id !== undefined && !isCopy) {
            this.id = json.id;
            if (this.sequencerEngine) {
                this.sequencerEngine.idToParameter[this.id] = this;
            }
        }

        if (json.lfos !== undefined &&
            json.lfos.length > 0 &&
            subSequence !== undefined) {
            json.lfos.forEach(
                i => {
                    let _lfo = subSequence.lfos[i].lfo;
                    if (this.lfos.includes(_lfo)) {
                        // its already connected but id might have changed
                        _lfo.replaceParameterIds(oldId, this.id, this);
                    }
                    this.connectToLFO(_lfo);
                });
        }

        if (json.envelopes !== undefined &&
            json.envelopes.length > 0 &&
            subSequence !== undefined) {
            json.envelopes.forEach(
                i => {
                    let env = subSequence.envelopes[i];
                    if (this.envelopes.includes(env)) {
                        env.replaceParameterIds(oldId, this.id, this);
                    } else
                        this.connectToEnvelope(env);
                });
        }

        if (json.value !== undefined) {
           bulk = this.set(json.value, true, true);
        }
        
        if (json.keyScaling) {
            this.keyScaling = new KeyScaling(this, json.keyScaling.min, json.keyScaling.max);
        }

        if (json.patternToValue !== undefined) {
            this.unlockedValue = json.unlockedValue !== undefined ?
                convertValue(json.unlockedValue) :
                this.value;
            this.patternToValue = convertPatternToValue(json.patternToValue);
            if (this.isPatternLocked() && this.patternToValue[0] !== undefined) {
                bulk = this.set(this.patternToValue[0], true, true);
            } else if (this.unlockedValue !== undefined) {
                bulk = this.set(this.unlockedValue, true, true);
            }
        }

        if (json.paletteToValue !== undefined) {
            this.paletteToValue = convertPatternToValue(json.paletteToValue);
        }

        if (json.patternToAutomation) {
            for (let i in json.patternToAutomation) {
                this.patternToAutomation[i] = new Automations(this);
                this.patternToAutomation[i].fromJson(json.patternToAutomation[i]);
            }
            this.unlockedAutomation.fromJson(json.unlockedAutomation);
        }

        this.loadModsFromJson(json);
        let b = {};
        b[bulk.id] = bulk.value;

        if (json.depth !== undefined) {
            if (!this.depthParameter) {
                this.initDepth();
            }
            this.depthParameter.fromJson(json.depth, sequencerEngine, isCopy);
        } else if (json._depth !== undefined) {
            this._depth = json._depth;
        }

        if (json.mixAutomation) {
            this.mixAutomation.fromJson(json.mixAutomation, false);
        }

        if (json.min !== undefined) {
            this.min = json.min;
        }

        if (json.max !== undefined) {
            this.max = json.max;
        }

        if (json.stepToValue) {
            this.stepToValue = json.stepToValue;
        }

        return b;
    }

    initDepth() {
        let depthValue = this._depth !== undefined ? this._depth :  1;
        this.depthSignal = new Tone.Signal(0);
        this.depthParameter = new AudioParamValueParameter(
            'depth', this.depthSignal, {
                min: 0,
                max: 1,
                defaultValue: depthValue,
                automationRate: A_RATE
            },
            0.2
        );

        if (this.context) {
            this.depthParameter.setContext(this.context);
        }
        this.depthParameter.setStore(this.store);
    }

    connectToLFO(lfo, isUx=false) {
        super.connectToLFO(lfo);
        if (isUx) {
            this.set(this.value, true, true);
        }
    }
    
    disconnectFromLFO(lfo, isUx=false, disconnectFromDepth) {
        super.disconnectFromLFO(lfo, false, disconnectFromDepth);
        if (isUx) {
            this.set(this.value, true, true);
        }
    }

    connectToEnvelope(env) {
        super.connectToEnvelope(env);
        this.set(this.value, true, true);
    }
    
    disconnectFromEnvelope(env, isUx=false, disconnectFromDepth) {
        super.disconnectFromEnvelope(env, false, disconnectFromDepth);
        if (isUx) {
            this.set(this.value, true, true);
        }
    }
}

class AudioParamValueParameter extends AbstractAudioParamParameter {
    constructor(name, audioParam, config={}, rampTime=0.1) {
        super(name, audioParam, (o, x, automation) => {
            if (!o || isNaN(x) || (!o.input && !config.noInput)) {
                return;
            }
            if (window.sequencerEngine &&
                window.sequencerEngine.playing) {
                if (!isNaN(x)) {
                    let currentTime = Tone.Master.context.isOffline ?
                        Tone.Master.context._currentTime : 
                        Tone.Master.context.currentTime; 
                    o.linearRampToValueAtTime(x, currentTime + rampTime);
                }
            } else {
                o.value = x;
            }
        }, config);
        this.hasAudioParamSetter = false;
        this.automationRate = A_RATE;
    }
}

class AudioParamPercentageValueParameter extends AudioParamValueParameter {
    constructor(name, audioParam, config={}) {
        config.unit = '%';
        super(name, audioParam, config);
        this.hasAudioParamSetter = false;
    }
}


export {AbstractAudioParamParameter, AudioParamPercentageValueParameter, AudioParamValueParameter};
